<template>
  <LModal
    :visible="visible"
    class="task-modal"
    centered
    scrollable
    size="lg"
    :title="
      createMode ? t('components.TaskModal.headline') : t('components.TaskModal.headlineChange')
    "
    :submit="
      async () => {
        await $emit('ok');
        return { success: true };
      }
    "
    @hidden="$emit('update:visible', false)"
    @show="$emit('update:visible', true)"
    @update:visible="$emit('update:visible', $event)"
  >
    <template #default="{ submit }">
      <TaskModalComponent :task="task" :event="event" @save="submit">
        <slot />
      </TaskModalComponent>
    </template>
    <template #footer="{ validation: { valid } }">
      <div class="text-right">
        <LISAButton class="mr-2 p-button-secondary" @click="$emit('update:visible', false)">
          <i class="fas fa-times mr-1"></i>
          {{ t("global.cancel") }}
        </LISAButton>
        <LISAButton
          v-if="isIHActive && !createMode"
          :disabled="!valid || isEventArchived === true"
          class="mr-2 p-button-info"
          @click="IHOrderHandler"
        >
          <i class="fas fa-chevron-right mr-1"></i>
          {{ t("license.ih.ih.forwardToIH") }}
          <InfoTooltip
            placement="topleft"
            class="ml-1"
            :text="t('license.ih.ih.forwardToIHText').toString()"
          ></InfoTooltip>
        </LISAButton>
        <AccessRightWrapper right="sqdc.write" class="inline">
          <template v-if="workflowConnections.length < 3">
            <LISAButton
              v-for="(workflowConnection, wfCIndex) in workflowConnections"
              :key="`wfConn${wfCIndex}`"
              class="mr-2"
              :disabled="
                !valid ||
                (workflowConnection.inputNode.shorthand == null &&
                  workflowConnection.inputNode.shorthandId != null) ||
                isEventArchived === true
              "
              @click="goToWorkflowStep(workflowConnection)"
            >
              <i class="fas fa-arrow-right mr-1"></i>
              {{ workflowConnection.settings.buttonText }}
              <InfoTooltip
                v-if="
                  workflowConnection.inputNode.shorthand == null &&
                  workflowConnection.inputNode.shorthandId != null
                "
                placement="topleft"
                :text="t('components.TaskModal.invalidShorthand').toString()"
              />
            </LISAButton>
          </template>
          <SplitButton
            v-else
            :model="splitButtonData"
            icon="fas fa-arrow-right"
            :disabled="workflowConnections.length < 1 || isEventArchived === true || !valid"
            :label="splitButtonData[0].label"
            class="pr-2"
            @click="splitButtonData[0].command()"
          />

          <LISAButton
            :disabled="!valid || isEventArchived === true"
            class="p-button-success"
            data-testid="TaskModalCreate"
            @click="$emit('ok')"
          >
            <i class="fas fa-save mr-1" />
            {{ createMode ? t("global.create") : t("global.save") }}
          </LISAButton>
        </AccessRightWrapper>
      </div>
    </template>
  </LModal>
</template>

<script setup lang="ts">
import { type PropType, computed, inject } from "vue";
import { useTranslation } from "@/language/translation";
import LISAButton from "@LISAPrime/LISAButton.vue";
import SplitButton from "primevue/splitbutton";
import type { MenuItem } from "primevue/menuitem";
import {
  type ActionListActionFields_Task_Fragment,
  type EventListEventFieldsFragment,
  TaskStatus,
  TaskTyp,
  useCreateTaskMutation,
} from "@/graphql";
import TaskModalComponent from "./TaskModalComponent.vue";
import { CommonTaskAdditionalComment } from "@/views/event-action/EventActionBoardInjectionKeys";
import InfoTooltip from "@/components/shared/InfoTooltip.vue";
import { createShorthandAction } from "@/modules/sqdc/helper/CreateShorthand";
import LModal from "@/components/LModal.vue";
import type { EditTaskType } from "../helper/DefaultTask";
import AccessRightWrapper from "@/components/AccessRightWrapper.vue";
import type { EventEditType } from "../../event/modal/EventEditType";

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  createMode: {
    type: Boolean,
    required: true,
  },
  task: {
    type: Object as PropType<EditTaskType>,
    required: true,
  },
  event: {
    type: Object as PropType<EventListEventFieldsFragment | EventEditType>,
    required: true,
  },
});
const emit = defineEmits(["update:visible", "ok", "nextWorkflowStep", "open:ihOrderModal"]);

const { t } = useTranslation();
const isEventArchived = computed(() => props.event.deletedAt != null);

const isIHActive = computed(
  () =>
    props.task.type === TaskTyp.common &&
    ["IHFailureNotice", "IHMaintenance"].includes(props.event.__typename!),
);
const additionalComment = inject(CommonTaskAdditionalComment, null);
async function IHOrderHandler() {
  if (props.task) {
    if (additionalComment != null) {
      additionalComment.value = t("license.ih.ih.commentText").toString();
    }
    props.task.status = TaskStatus.act;
    /**
     * Emit "ok", receiving end will save the task and then run this function
     * You can find the trigger in UpdateTaskModal.vue:161
     */
    emit("ok", () => {
      emit("open:ihOrderModal");
    });
  }
}

const { mutate } = useCreateTaskMutation({});

async function goToWorkflowStep(
  connection: NonNullable<ActionListActionFields_Task_Fragment["drawFlowNode"]>["connections"][0],
) {
  let task;
  if (connection.inputNode.shorthandId != null) {
    if (connection.inputNode.shorthand == null) {
      throw Error("shorthand was deleted");
    }
    task = createShorthandAction(
      props.event,
      mutate,
      connection.input,
      connection.inputNode.shorthand,
    );
  }

  emit("nextWorkflowStep", connection, task);
}

const workflowConnections = computed(() => {
  const connections = props.task.drawFlowNode?.connections ?? [];
  return [...connections].sort((a, b) => a.settings.buttonIndex - b.settings.buttonIndex);
});

const splitButtonData = computed<(Omit<MenuItem, "label"> & { label: string })[]>(() => [
  ...workflowConnections.value.map((el) => ({
    label: el.settings.buttonText,
    command: () => goToWorkflowStep(el),
    disabled: el.inputNode.shorthand == null,
  })),
]);
</script>
